import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "hooks/useToast";
import { Link } from "react-router-dom";
import { Routes } from "router/routes";
import { PrecisionLogoMain, hireBackgroundImage } from "assets/website";
import { InstagramIcon, TwitterIcon, WhatsappIcon, PhoneCallIcon, EnvelopeTwoIcon, SendIconFilled } from "assets/icons";
import { FacebookIcon, LinkedInIcon } from "assets/website";
import { fadedPrecisionLogo } from "assets/images";

const pills = [
  { label: "Audit your current team", color: "#605BFF" },
  { label: "Run your first assessment", color: "#DB8400" },
  { label: "Make your next hire count", color: "#11AF36" },
];

const quickLinks = [
  { label: "How it Works", to: "/how-it-works" },
  { label: "Use cases", to: "/use-cases" },
  { label: "Pricing", to: "/pricing" },
  { label: "FAQs", to: "/faqs" },
  { label: "Contact Support", to: "/contact-us" },
  { label: "Talent Enablement", to: "/talent-enablement" },
];

const legalLinks = [
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms & Conditions", to: "/terms-of-use" },
];

const contactItems = [
  { href: "tel:+2348031301167", Icon: PhoneCallIcon, label: "+2348031301167" },
  { href: "mailto:contact@precisionmatch.ai", Icon: EnvelopeTwoIcon, label: "contact@precisionmatch.ai" },
];

interface FooterProps {
  /** Show the "Your First Assessment is on US" CTA above the footer. Defaults to true. */
  showCta?: boolean;
}

const Footer: React.FC<FooterProps> = ({ showCta = true }) => {
  const navigate = useNavigate();
  const { openToast } = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] = useState("");

  const handleTryFree = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      openToast({ show: true, heading: "Input required", text: "Please describe what you want to assess.", type: "error", timeOut: 4000 });
      return;
    }
    if (trimmed.length < 5) {
      openToast({ show: true, heading: "Too short", text: "Please enter at least 5 characters.", type: "error", timeOut: 4000 });
      return;
    }
    navigate(Routes.signup);
  };

  return (
    <>
      {/* ── CTA: Your First Assessment is on US ── */}
      {showCta && (
      <section
        className="relative overflow-hidden py-12 md:py-16 bg-black"
        style={{
          backgroundImage: `url(${hireBackgroundImage})`,
          backgroundSize: "70%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/75" />

        <div className="relative z-10 container mx-auto xl:px-8 lg:px-4 px-4 flex flex-col items-center text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-6 max-w-[805px]">
            Your First Assessment is on US
          </h2>

          {/* Feature pills */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-16">
            {pills.map((pill, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-white text-sm font-medium"
                style={{ backgroundColor: pill.color }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M2.5 7L5.5 10L11.5 4"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {pill.label}
              </span>
            ))}
          </div>

          {/* Assessment textarea widget */}
          <div className="w-full max-w-[805px] bg-white rounded-xl shadow-2xl overflow-hidden">
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="What do you want to assess today?"
              className="w-full h-[140px] p-5 text-base text-[#344054] placeholder-[#98A2B3] resize-none focus:outline-none bg-transparent leading-relaxed"
            />
            <div className="flex items-center justify-end px-4 pb-4">
              <button
                onClick={handleTryFree}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#012169] hover:bg-[#011550] text-white text-sm font-semibold rounded-lg transition-all duration-200"
              >
                Try for Free
                <SendIconFilled className="w-5 h-5 [&_path]:fill-none [&_path]:stroke-white [&_path]:stroke-[1.5px]" />
              </button>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── Footer ── */}
      <footer
        className="relative bg-black text-white py-16 overflow-hidden"
      >
        {/* Tiled logo watermark */}
        <div className="absolute inset-0 pointer-events-none select-none overflow-hidden opacity-[0.15]">
          <div className="flex flex-wrap" style={{ gap: "60px", padding: "32px" }}>
            {Array.from({ length: 60 }).map((_, i) => (
              <img key={i} src={fadedPrecisionLogo} alt="" style={{ width: "250px", height: "auto", flexShrink: 0 }} />
            ))}
          </div>
        </div>

        <div className="relative z-10 sm:w-[90%] mx-auto sm:px-0 px-5 flex flex-col md:flex-row justify-between gap-12">
          {/* Company Info */}
          <div className="max-w-[300px]">
            <PrecisionLogoMain className="mb-4" />
            <p className="text-[#FCFCFD] mb-8 text-lg leading-relaxed">
              Precision AI helps companies identify truly qualified candidates by
              automatically scoring, ranking, and assessing applicants.
            </p>
            <div className="flex gap-4 items-center">
              <a href="https://www.instagram.com/precisionmatch/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:opacity-80 transition-opacity">
                <InstagramIcon className="w-5 h-5 [&_path]:fill-white" />
              </a>
              <a href="https://x.com/precisionmatch" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="hover:opacity-80 transition-opacity">
                <TwitterIcon className="w-5 h-5 [&_path]:fill-white" />
              </a>
              <a href="https://www.facebook.com/share/18MgqCRfxZ/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:opacity-80 transition-opacity">
                <FacebookIcon className="w-5 h-5 text-white" />
              </a>
              <a href="https://ng.linkedin.com/company/precision-match" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="hover:opacity-80 transition-opacity">
                <LinkedInIcon className="w-5 h-5 text-white" />
              </a>
              <a href="https://wa.me/2348031301167" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="hover:opacity-80 transition-opacity">
                <WhatsappIcon className="w-5 h-5 [&_path]:fill-white" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-medium mb-6">Quick Links</h3>
            <ul className="space-y-4">
              {quickLinks.map(({ label, to }) => (
                <li key={to + label}>
                  <Link to={to} className="text-[#FCFCFD] text-lg hover:opacity-80 transition-opacity">{label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-xl font-medium mb-6">Legal</h3>
            <ul className="space-y-4">
              {legalLinks.map(({ label, to }) => (
                <li key={to}>
                  <Link to={to} className="text-[#FCFCFD] text-lg hover:opacity-80 transition-opacity">{label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xl font-medium mb-6">Contact</h3>
            <ul className="space-y-4">
              {contactItems.map(({ href, Icon, label }) => (
                <li key={href}>
                  <a href={href} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                    <Icon className="w-5 h-5 shrink-0" />
                    <span className="text-[#FCFCFD] text-lg">{label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="relative z-10 sm:w-[90%] mx-auto sm:px-0 px-5 mt-16 pt-8">
          <p className="text-[#FCFCFD] text-base flex items-center gap-1.5">
            Copyright
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.25" />
              <path
                d="M10.5 6.2C10.07 5.46 9.1 5 8 5C6.34 5 5 6.12 5 8C5 9.88 6.34 11 8 11C9.1 11 10.07 10.54 10.5 9.8"
                stroke="currentColor"
                strokeWidth="1.25"
                strokeLinecap="round"
              />
            </svg>
            {new Date().getFullYear()} PrecisionMatch
          </p>
        </div>
      </footer>
    </>
  );
};

export { Footer };
